import {
  GRID_PATTERN_IDS,
  MAX_GRID_HEIGHT_PX,
  MAX_GRID_WIDTH_PX,
  SELECTABLE_TOOLS,
} from '~/composables/grid/constants'
import { getShapeById } from '~/composables/shapes/utils'
import type { ShapeInterface } from '~/types/grid.types'

/**
 * Mobile clipboard: copy / cut / paste for the mobile edit toolbar.
 *
 * The desktop studio has no Paste button because its "Copy" is an interactive
 * stamp: the copied shapes become a ghost that follows the mouse and a click
 * drops them wherever the cursor is. A touch screen has no cursor to follow,
 * so mobile needs an explicit Paste, and it has to place the shapes itself.
 *
 * Placement is pattern aware. Every grid pattern has its own lattice of legal
 * tile positions (the square grid steps in whole tile widths, the 45 degree
 * grid steps along its diagonal), so the pasted tile is placed by stepping the
 * copied tile along the lattice of the pattern it lives on - not by a
 * hardcoded pixel offset, which is what used to leave the paste 1px off the
 * grid and stacked under the original.
 */

/** The axis-aligned footprint of a shape, honouring a 90/270 rotation. */
export function getItemDimensions(item: ShapeInterface) {
  const shapeId = item.metadata?.shape_id || item.attrs?.metadata?.shape_id
  const shapeDef = shapeId ? getShapeById(shapeId) : null
  const baseW = shapeDef?.w ?? item.width ?? item.attrs?.width ?? 120
  const baseH = shapeDef?.h ?? item.height ?? item.attrs?.height ?? 120
  const rotation = item.rotation ?? item.attrs?.rotation ?? 0
  const isTurned = Math.abs(rotation % 180) === 90

  return {
    width: isTurned ? baseH : baseW,
    height: isTurned ? baseW : baseH,
    x: item.x ?? item.attrs?.x ?? 0,
    y: item.y ?? item.attrs?.y ?? 0,
    rotation,
  }
}

/**
 * The step between neighbouring tiles on the current grid pattern.
 *
 * `snap` is the pattern's own tile lattice. The square and 3D grids step in
 * half-tile increments (the fine snap), so a tile that is one tile wide sits
 * two of those steps from its neighbour. The 45 degree grid steps in whole
 * lattice units on both axes.
 */
export function getLatticeStep(snap: { x: number; y: number }, gridPatternId?: string): { x: number; y: number } {
  const snapX = snap?.x || 30
  const snapY = snap?.y || 30

  if (gridPatternId === GRID_PATTERN_IDS.SQUARE_45) {
    // The 45 degree lattice: neighbouring tiles are `snap * 2` apart on
    // each axis (60 * sqrt(2) padding, halved).
    return { x: snapX * 2, y: snapY * 2 }
  }

  return { x: snapX, y: snapY }
}

/** Does the axis-aligned box of a shape overlap the box of another shape? */
function boxesOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
  tolerance = 1,
): boolean {
  const aLeft = a.x - a.width / 2
  const aRight = a.x + a.width / 2
  const aTop = a.y - a.height / 2
  const aBottom = a.y + a.height / 2
  const bLeft = b.x - b.width / 2
  const bRight = b.x + b.width / 2
  const bTop = b.y - b.height / 2
  const bBottom = b.y + b.height / 2

  return !(
    aRight <= bLeft + tolerance ||
    aLeft >= bRight - tolerance ||
    aBottom <= bTop + tolerance ||
    aTop >= bBottom - tolerance
  )
}

export interface FindPastePlacementOptions {
  items: ShapeInterface[]
  existingShapes: ShapeInterface[]
  gridPatternId?: string
  snap?: { x: number; y: number }
  startOffset?: number
  gridWidth?: number
  gridHeight?: number
  isCut?: boolean
}

export interface PastePlacement {
  /** Grid-relative anchor of the pasted group (the leader's top-left cell). */
  anchorX: number
  anchorY: number
  /** Absolute canvas offset applied to every pasted shape. */
  dx: number
  dy: number
}

/**
 * Find where the pasted shapes should land.
 *
 * A cut tile goes back where it came from when that spot is still free.
 * Otherwise the shapes are placed in the nearest free lattice position, so
 * pasting repeatedly fills the space next to the original instead of
 * stacking on top of it.
 */
export function findPastePlacement(options: FindPastePlacementOptions): PastePlacement | null {
  const {
    items,
    existingShapes,
    gridPatternId = GRID_PATTERN_IDS.SQUARE,
    snap = { x: 30, y: 30 },
    startOffset = 480,
    gridWidth = 1920,
    gridHeight = 1080,
    isCut = false,
  } = options

  if (items.length === 0) return null

  const leader = getItemDimensions(items[0])
  const step = getLatticeStep(snap, gridPatternId)

  // The existing tiles, as boxes, so a candidate can be tested against them.
  const existingBoxes = existingShapes.map((shape) => {
    const dim = getItemDimensions(shape)
    return { id: shape.id, x: dim.x, y: dim.y, width: dim.width, height: dim.height }
  })

  const boxesFor = (offset: { dx: number; dy: number }) =>
    items.map((item) => {
      const dim = getItemDimensions(item)
      return {
        id: item.id,
        x: dim.x + offset.dx,
        y: dim.y + offset.dy,
        width: dim.width,
        height: dim.height,
      }
    })

  const collides = (offset: { dx: number; dy: number }) => {
    const boxes = boxesFor(offset)
    for (const box of boxes) {
      // A shape must stay inside the playable area of the grid.
      if (box.x - box.width / 2 < startOffset || box.y - box.height / 2 < startOffset) return true
      if (box.x + box.width / 2 - startOffset > gridWidth) return true
      if (box.y + box.height / 2 - startOffset > gridHeight) return true

      for (const other of existingBoxes) {
        if (other.id === box.id) continue
        if (boxesOverlap(box, other)) return true
      }
    }
    return false
  }

  // A cut tile returns to its original spot when nothing moved into it.
  if (isCut && !collides({ dx: 0, dy: 0 })) {
    return {
      anchorX: leader.x - startOffset - leader.width / 2,
      anchorY: leader.y - startOffset - leader.height / 2,
      dx: 0,
      dy: 0,
    }
  }

  // Otherwise step out from the original position along the lattice. The
  // order is right, then down, then the diagonals, then left and up, so the
  // paste lands next to the original rather than across the canvas.
  const maxSteps = 24
  for (let s = 1; s <= maxSteps; s++) {
    const candidates = [
      { dx: s * step.x, dy: 0 },
      { dx: 0, dy: s * step.y },
      { dx: s * step.x, dy: s * step.y },
      { dx: -s * step.x, dy: 0 },
      { dx: 0, dy: -s * step.y },
      { dx: -s * step.x, dy: s * step.y },
      { dx: s * step.x, dy: -s * step.y },
      { dx: -s * step.x, dy: -s * step.y },
    ]

    for (const candidate of candidates) {
      if (!collides(candidate)) {
        return {
          anchorX: leader.x - startOffset - leader.width / 2 + candidate.dx,
          anchorY: leader.y - startOffset - leader.height / 2 + candidate.dy,
          dx: candidate.dx,
          dy: candidate.dy,
        }
      }
    }
  }

  return null
}

/**
 * Paste the clipboard onto the grid.
 *
 * The pasted shapes are new grid items with their own ids, they are selected
 * (so the user can immediately drag, rotate or recolour them), and the
 * placement is pushed onto the undo stack.
 */
export function executeMobilePaste(gridStore: any, projectStore?: any, runtimeConfig?: any): string[] {
  const clipboardItems = gridStore.clipboard?.items as ShapeInterface[] | undefined
  if (!clipboardItems || clipboardItems.length === 0) return []

  const isCut = gridStore.clipboard.action === 'cut'
  const startOffset = gridStore.startOffset || 0

  const placement = findPastePlacement({
    items: clipboardItems,
    existingShapes: gridStore.shapeList as ShapeInterface[],
    gridPatternId: gridStore.gridPattern?.id,
    // The PATTERN snap, not the fine snap: the fine snap is the drag
    // resolution inside a cell, while the pattern snap is the lattice the
    // tiles themselves sit on. Pasting has to land on that lattice.
    snap: gridStore.gridPattern?.snap || { x: 30, y: 30 },
    startOffset,
    gridWidth: gridStore.gridWidth,
    gridHeight: gridStore.gridHeight,
    isCut,
  })

  if (!placement) return []

  gridStore.saveState()

  const timestamp = Date.now()
  const shapesToAdd: ShapeInterface[] = []
  const newIds: string[] = []

  clipboardItems.forEach((item, index) => {
    const generatedId = `${timestamp}-${index}`
    const newX = (item.x ?? item.attrs?.x ?? 0) + placement.dx
    const newY = (item.y ?? item.attrs?.y ?? 0) + placement.dy

    const originalMetadata = item.metadata || item.attrs?.metadata || {}
    const metadata = { ...originalMetadata }
    // A pasted set must not reuse the original set_id, or the two groups
    // would move and delete as one.
    if (originalMetadata.set_id) {
      metadata.set_id = `set-${timestamp}`
    }

    const newShape: ShapeInterface = {
      ...item,
      id: generatedId,
      x: newX,
      y: newY,
      metadata,
      attrs: {
        ...(item.attrs || {}),
        id: generatedId,
        x: newX,
        y: newY,
        opacity: 1,
        fill: null,
        collision: false,
        rotation: item.rotation ?? item.attrs?.rotation ?? 0,
        metadata,
      },
    }

    shapesToAdd.push(newShape)
    newIds.push(generatedId)
  })

  gridStore.addShapes(shapesToAdd)
  gridStore.expandGridToFitShapes()
  gridStore.selectItemsOnGrid(newIds)
  gridStore.updateSelectedTool(SELECTABLE_TOOLS.SELECT)

  // A cut tile is consumed by the paste. A copied tile stays in the
  // clipboard so it can be pasted again.
  if (isCut) {
    gridStore.clearClipboard()
  }

  if (runtimeConfig?.public?.autoSaveOnChange && projectStore?.save) {
    projectStore.save(true, true)
  }

  return newIds
}
